import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { ContractViewModel } from '@models/Contract';
import { SpinnerPage, Card, CardHeader, CardBody } from '@common/components';
import { PropertyInfo, CoverageStep, CustomerInfo, AffiliatesInfo } from 'src/contract';
import { PriceAdjustmentsStep } from 'src/contract/_components/coverage/price-adjustments-step.container';
import { EmployeeDiscountViewModel, GetPriceAdjustmentMetadata } from '@models/Product';
import { StatesViewModel, blankStatesVM } from '@models/common.models';
import { history } from 'src';
import { getRoute, RouteDefinitions } from '@common/route-definitions';
import { CommentType } from '@models/Comment';
import { Comment } from 'src/comments';

export interface OrderEditProperties extends RouteComponentProps<{ id: number }> {
  readonly contractVM: ContractViewModel;

  readonly isEmployeeDiscountEnabled: boolean;
  readonly addressChanged: boolean;
  readonly saleTypeId: number;
  readonly propertyTypeId: number;
  readonly employeeDiscounts: Array<EmployeeDiscountViewModel>;
  readonly state: StatesViewModel;
  readonly zipCode: string;
  readonly hasPropertyManagement: boolean;
  readonly hasAgent: boolean;
  readonly loading: boolean;
  readonly commentText?: string;
  readonly commentCallerName?: string;
  readonly isOpenContract?: boolean;
  readonly loadOrderEditContract: (id: number) => void;
  readonly checkIsTabValid: (fromTab: number) => boolean;
  readonly saveContractOrder: (id: number) => Promise<void>;
  readonly loadCoverageStep: (
    adjustmentMetaData: GetPriceAdjustmentMetadata,
    propertyTypeId: number,
    saleTypeId: number,
    isRenewal: boolean
  ) => void;
  readonly loadPriceAdjustmentStep: (adjustmentMetaData: GetPriceAdjustmentMetadata, isRenewal: boolean) => void;
  readonly validateComment: () => void;
  readonly verifyAddress: (callback: Function) => void;
  readonly setIsStoringStep: (isStoring: boolean, stepNumber: number) => Promise<void>;
}

interface OrderEditState {
  isSaving: boolean;
  saveAttempted: boolean;
  allValid: boolean;
}

const PROPERTY_INFO_STEP = 0;
const COVERAGE_INFO_STEP = 1;
const CUSTOMER_INFO_STEP = 2;
const AFFILIATES_INFO_STEP = 3;
const PRICE_ADJUSTMENT_STEP = 4;

export class OrderEditComponent extends React.Component<OrderEditProperties, OrderEditState> {
  private hiddenButtons: Array<HTMLButtonElement> = [];

  constructor(props: OrderEditProperties) {
    super(props);

    this.state = {
      isSaving: false,
      saveAttempted: false,
      allValid: false
    };
  }

  isOpenOrder = (): boolean => {
    return false;
  };

  componentWillMount() {
    if (this.props.match && this.props.match.params.id) {
      this.props.loadOrderEditContract(this.props.match.params.id);
    }
    const adjustmentMetaData: GetPriceAdjustmentMetadata = {
      hasPropertyManagement: this.props.hasPropertyManagement,
      hasAgent: this.props.hasAgent,
      customerId: this.props.contractVM.buyerId,
      stateAbbreviation: (this.props.state || blankStatesVM).abbreviation,
      zipCode: this.props.zipCode,
      hasEmployeeDiscounts: false // overwritten from state inside trigger get adjustments
    };
    this.props.loadPriceAdjustmentStep(adjustmentMetaData, false);
  }

  getButtonRef = (step: number) => {
    return (button: HTMLButtonElement) => {
      this.hiddenButtons[step] = button;
    };
  };

  validate = (fromTabIndex: number) => {
    let button = this.hiddenButtons[fromTabIndex];
    if (button) {
      button.click();
    }
  };

  validateAll = () => {
    let anyInvalid = false;
    for (const key of Object.keys(this.hiddenButtons)) {
      this.props.setIsStoringStep(true, +key);
      if (+key === PROPERTY_INFO_STEP) {
        this.saveAddress();
      }

      this.validate(+key);
      if (!this.props.checkIsTabValid(+key)) {
        this.setState({
          allValid: false
        });
        anyInvalid = true;
      }
    }
    this.props.validateComment();

    const commentText = this.props.commentText;
    const commentCallerName = this.props.commentCallerName;
    if (commentText && commentText.length > 0 && commentCallerName && commentCallerName.length > 0 && !anyInvalid) {
      this.setState({ allValid: true });
    }
  };

  handleSaveClick = () => {
    if (!this.state.isSaving) {
      this.setState({ isSaving: true, saveAttempted: true });
      this.validateAll();

      const commentText = this.props.commentText;
      const commentCallerName = this.props.commentCallerName;
      if (
        commentText &&
        commentText.length > 0 &&
        commentCallerName &&
        commentCallerName.length > 0 &&
        this.state.allValid
      ) {
        this.validateAndFinish();
      }
    }
  };

  finishSave = () => {
    const commentText = this.props.commentText;
    const commentCallerName = this.props.commentCallerName;
    if (
      commentText &&
      commentText.length > 0 &&
      commentCallerName &&
      commentCallerName.length > 0 &&
      this.state.isSaving &&
      this.state.allValid
    ) {
      if (this.props.match && this.props.match.params.id) {
        this.props.saveContractOrder(this.props.match.params.id);
      }
    } else {
      this.setState({ isSaving: false });
      const errors = document.getElementsByClassName('error');
      if (errors.length > 0) {
        const error = errors[0] as HTMLElement;
        const position = error.offsetTop;
        document.getElementsByTagName('main')[0].scrollTo(0, position - 30);
      }
    }
  };

  validateAndFinish = () => {
    const checkIsTabValid = this.props.checkIsTabValid;
    this.setState(
      {
        allValid:
          checkIsTabValid(COVERAGE_INFO_STEP) &&
          checkIsTabValid(CUSTOMER_INFO_STEP) &&
          checkIsTabValid(AFFILIATES_INFO_STEP) &&
          checkIsTabValid(PRICE_ADJUSTMENT_STEP),
        isSaving: true
      },
      this.finishSave
    );
  };

  saveAddress = () => {
    this.props.addressChanged ? this.props.verifyAddress(this.validateAndFinish) : this.validateAndFinish();
  };

  failCallback = () => {
    this.setState({ isSaving: false });
  };

  renderCommentBox = () => {
    return <Comment entityId={-1} commentType={CommentType.Contract} hideSave={true} showCallerNameField={true} />;
  };

  renderErrorIcon = (index: number) => {
    return (
      this.state.saveAttempted &&
      !this.props.checkIsTabValid(index) && (
        <>
          <i className={['fas fa-exclamation-triangle'].join(' ')} />
          {`  `}
        </>
      )
    );
  };
  renderPropertyInfoCard() {
    return (
      <Card defaultClosed={true} hasError={this.state.saveAttempted && !this.props.checkIsTabValid(0)}>
        <CardHeader>
          <div className="row m-5">
            <div className="col-xs-12">
              <h3 className="m-5">
                {this.renderErrorIcon(PROPERTY_INFO_STEP)}
                Contact and Property Info
              </h3>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <PropertyInfo
            buttonRef={this.getButtonRef(PROPERTY_INFO_STEP)}
            currentStep={PROPERTY_INFO_STEP}
            failCallback={this.failCallback}
            isOrderEdit={true}
          />
        </CardBody>
      </Card>
    );
  }
  renderCoverageCard() {
    return (
      <Card defaultClosed={true} hasError={this.state.saveAttempted && !this.props.checkIsTabValid(COVERAGE_INFO_STEP)}>
        <CardHeader>
          <div className="row m-5">
            <div className="col-xs-12">
              <h3 className="m-5">
                {this.renderErrorIcon(COVERAGE_INFO_STEP)}
                Coverage
              </h3>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <CoverageStep
            buttonRef={this.getButtonRef(COVERAGE_INFO_STEP)}
            currentStep={COVERAGE_INFO_STEP}
            isEmployeeDiscountEnabled={this.props.isEmployeeDiscountEnabled}
            saleTypeId={this.props.saleTypeId}
            propertyTypeId={this.props.propertyTypeId}
            employeeDiscounts={this.props.employeeDiscounts}
            state={this.props.state}
            hasPropertyManagement={this.props.hasPropertyManagement}
            hasAgent={this.props.hasAgent}
            isOrderEdit={true}
          />
        </CardBody>
      </Card>
    );
  }
  renderCustomerInfoCard() {
    return (
      <Card defaultClosed={true} hasError={this.state.saveAttempted && !this.props.checkIsTabValid(CUSTOMER_INFO_STEP)}>
        <CardHeader>
          <div className="row m-5">
            <div className="col-xs-12">
              <h3 className="m-5">
                {this.renderErrorIcon(CUSTOMER_INFO_STEP)}
                Customer Info
              </h3>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <CustomerInfo
            buttonRef={this.getButtonRef(CUSTOMER_INFO_STEP)}
            currentStep={CUSTOMER_INFO_STEP}
            isOrderEdit={true}
          />
        </CardBody>
      </Card>
    );
  }
  renderAffiliatesInfoCard() {
    return (
      <Card defaultClosed={true} hasError={this.state.saveAttempted && !this.props.checkIsTabValid(3)}>
        <CardHeader>
          <div className="row m-5">
            <div className="col-xs-12">
              <h3 className="m-5">
                {this.renderErrorIcon(AFFILIATES_INFO_STEP)}
                Affiliates Info
              </h3>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <AffiliatesInfo
            buttonRef={this.getButtonRef(AFFILIATES_INFO_STEP)}
            currentStep={AFFILIATES_INFO_STEP}
            isOrderEdit={true}
          />
        </CardBody>
      </Card>
    );
  }
  renderPriceAdjustmentsCard() {
    return (
      <Card
        defaultClosed={true}
        hasError={this.state.saveAttempted && !this.props.checkIsTabValid(PRICE_ADJUSTMENT_STEP)}
      >
        <CardHeader>
          <div className="row m-5">
            <div className="col-xs-12">
              <h3 className="m-5">
                {this.renderErrorIcon(PRICE_ADJUSTMENT_STEP)}
                Price Adjustments
              </h3>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <PriceAdjustmentsStep
            buttonRef={this.getButtonRef(PRICE_ADJUSTMENT_STEP)}
            currentStep={PRICE_ADJUSTMENT_STEP}
            isEmployeeDiscountEnabled={this.props.isEmployeeDiscountEnabled}
            saleTypeId={this.props.saleTypeId}
            propertyTypeId={this.props.propertyTypeId}
            employeeDiscounts={this.props.employeeDiscounts}
            state={this.props.state}
            hasPropertyManagement={this.props.hasPropertyManagement}
            hasAgent={this.props.hasAgent}
            isOrderEdit={true}
          />
        </CardBody>
      </Card>
    );
  }
  handleEditChange() {
    const adjustmentMetaData: GetPriceAdjustmentMetadata = {
      hasPropertyManagement: this.props.hasPropertyManagement,
      hasAgent: this.props.hasAgent,
      customerId: this.props.contractVM.buyerId,
      stateAbbreviation: (this.props.state || blankStatesVM).abbreviation,
      zipCode: this.props.zipCode,
      hasEmployeeDiscounts: false // overwritten from state inside trigger get adjustments
    };
    this.props.loadPriceAdjustmentStep(adjustmentMetaData, false);
  }
  render() {
    if (this.props.loading || !this.props.contractVM.id || this.state.isSaving) {
      return <SpinnerPage />;
    }
    return (
      <>
        {this.renderPropertyInfoCard()}
        {this.renderCoverageCard()}
        {this.renderCustomerInfoCard()}
        {this.renderAffiliatesInfoCard()}
        {this.renderPriceAdjustmentsCard()}

        {this.renderCommentBox()}
        <div className="row between-xs">
          <button
            className="btn btn-primary"
            onClick={() => {
              history.push(
                this.props.match && this.props.match.params.id
                  ? getRoute(RouteDefinitions.contract.Details, {
                      id: +this.props.match.params.id
                    })
                  : RouteDefinitions.dashboard
              );
            }}
          >
            Cancel
          </button>
          {this.state.allValid}
          <div>
            <button className="btn btn-primary" onClick={this.handleSaveClick} disabled={this.state.isSaving}>
              Save
            </button>
          </div>
        </div>
      </>
    );
  }
}



// WEBPACK FOOTER //
// ./src/contract/_components/order-edit/_components/order-edit.component.tsx